import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { CircleAlert, LoaderCircle, ShieldX } from "lucide-react";

import { apiFetch } from "@/lib/api-client";
import { useAuth } from "@/lib/auth-provider";
import { formatDateTime } from "@/lib/i18n";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

type AccessRequestStatus = "pending" | "approved" | "denied";

interface AccessRequest {
  id: string;
  organizationId: string | null;
  requestedByUserId: string;
  requesterName: string;
  requesterEmail: string;
  requestedRole: string;
  resource: string;
  resourcePath: string | null;
  message: string | null;
  status: AccessRequestStatus;
  reviewedByUserId: string | null;
  reviewNote: string | null;
  reviewedAt: string | null;
  notificationSentAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export default function PermissionDeniedPage() {
  const { session } = useAuth();
  const [searchParams] = useSearchParams();
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resource = searchParams.get("resource")?.trim() || "this area";
  const requiredRole = searchParams.get("requiredRole")?.trim() || "admin";
  const returnTo = searchParams.get("returnTo")?.trim() || "/dashboard";
  const organizationId = session?.activeOrganizationId ?? null;

  const loadRequests = useCallback(async () => {
    const params = new URLSearchParams();
    if (organizationId) {
      params.set("organizationId", organizationId);
    }

    setIsLoading(true);
    try {
      const result = await apiFetch<{ data: { requests: AccessRequest[] } }>(
        `/v1/platform/access-requests${params.toString() ? `?${params.toString()}` : ""}`,
      );
      setRequests(result.data.requests ?? []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load access requests.");
    } finally {
      setIsLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    void loadRequests();
  }, [loadRequests]);

  const latestMatchingRequest = useMemo(() => {
    return requests.find((request) =>
      request.requestedRole === requiredRole
      && request.resource === resource
      && (request.resourcePath ?? returnTo) === returnTo,
    ) ?? null;
  }, [requests, requiredRole, resource, returnTo]);

  const handleRequestAccess = async () => {
    setIsSubmitting(true);
    try {
      const result = await apiFetch<{ data: AccessRequest }>("/v1/platform/access-requests", {
        method: "POST",
        body: JSON.stringify({
          organizationId,
          requestedRole: requiredRole,
          resource,
          resourcePath: returnTo,
          message: message.trim() || null,
        }),
      });
      setRequests((current) => [result.data, ...current.filter((item) => item.id !== result.data.id)]);
      setMessage("");
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit access request.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 px-6 py-12">
        <Skeleton className="h-8 w-56" />
        <SkeletonCard className="h-48" />
      </div>
    );
  }

  const statusTone = latestMatchingRequest?.status === "approved"
    ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-200"
    : latestMatchingRequest?.status === "denied"
      ? "border-rose-500/25 bg-rose-500/10 text-rose-200"
      : "border-amber-500/25 bg-amber-500/10 text-amber-200";

  return (
    <div className="mx-auto max-w-4xl px-6 py-12">
      <div className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-background)] p-8 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-500/10 text-rose-300">
            <ShieldX className="h-7 w-7" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">Access control</p>
            <h1 className="mt-2 text-3xl font-semibold text-[var(--color-foreground)]">Access Restricted</h1>
            <p className="mt-3 max-w-2xl text-sm text-[var(--color-muted-foreground)]">
              You need <span className="font-semibold text-[var(--color-foreground)]">{requiredRole}</span> access to view{" "}
              <span className="font-semibold text-[var(--color-foreground)]">{resource}</span>.
            </p>
          </div>
        </div>

        {error ? (
          <div className="mt-6 flex items-start gap-2 rounded-xl border border-rose-500/25 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
            <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}

        {latestMatchingRequest ? (
          <div className={`mt-6 rounded-2xl border px-5 py-4 ${statusTone}`}>
            <p className="text-sm font-semibold">Current request: {latestMatchingRequest.status}</p>
            <p className="mt-1 text-sm">
              Submitted {formatDateTime(latestMatchingRequest.createdAt)}
              {latestMatchingRequest.reviewedAt ? ` • Reviewed ${formatDateTime(latestMatchingRequest.reviewedAt)}` : ""}
            </p>
            {latestMatchingRequest.reviewNote ? (
              <p className="mt-2 text-sm">{latestMatchingRequest.reviewNote}</p>
            ) : null}
          </div>
        ) : null}

        <div className="mt-8 grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
          <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)]/40 p-5">
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Request Access</h2>
            <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
              Submit a review request to the platform admin. Approval grants the requested role directly on your account.
            </p>
            <label className="mt-4 block">
              <span className="mb-1 block text-sm font-medium text-[var(--color-foreground)]">Reason</span>
              <textarea
                rows={5}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
                placeholder="Explain the operational need and why this level of access is required."
              />
            </label>
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => void handleRequestAccess()}
                disabled={isSubmitting || !organizationId || latestMatchingRequest?.status === "pending"}
                className="inline-flex items-center gap-2 rounded-xl bg-[var(--color-primary)] px-4 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
                Request Access
              </button>
              <Link
                to="/dashboard"
                className="inline-flex items-center rounded-xl border border-[var(--color-border)] px-4 py-2.5 text-sm font-medium text-[var(--color-foreground)]"
              >
                Back to dashboard
              </Link>
            </div>
            {!organizationId ? (
              <p className="mt-3 text-sm text-[var(--color-warning)]">
                No active workspace is attached to your session, so access requests are temporarily unavailable.
              </p>
            ) : null}
          </div>

          <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">What happens next</h2>
            <ol className="mt-4 space-y-3 text-sm text-[var(--color-muted-foreground)]">
              <li>1. The request is logged against your active organization.</li>
              <li>2. A super-admin reviews the request from the platform admin panel.</li>
              <li>3. Approval updates your user role and sends a transactional email confirmation.</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
